import { clearSession, setSession } from './sessionStore';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const CLIENT_PORTAL_FLAGS_KEY = 'clientPortalFlags';
const TOKEN_STORAGE_PREFIX = 'enc:';

function isJwtFormat(value) {
  const parts = String(value ?? '').split('.');
  return parts.length === 3 && parts[0].length > 0 && parts[1].length > 0;
}

function encodeTokenForStorage(token) {
  const value = String(token ?? '').trim();
  if (!value) return '';
  try {
    return `${TOKEN_STORAGE_PREFIX}${btoa(value)}`;
  } catch {
    return '';
  }
}

function decodeTokenFromStorage(stored) {
  if (!stored) return null;

  if (stored.startsWith(TOKEN_STORAGE_PREFIX)) {
    try {
      const decoded = atob(stored.slice(TOKEN_STORAGE_PREFIX.length));
      return decoded || null;
    } catch {
      return null;
    }
  }

  // Legacy plain JWT from cookie/localStorage migration.
  if (isJwtFormat(stored)) return stored;
  return null;
}

function readClientPortalFlags() {
  try {
    const raw = sessionStorage.getItem(CLIENT_PORTAL_FLAGS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      allowUserAppOnWeb: Boolean(parsed.allowUserAppOnWeb),
      allowAccessWithoutPayment: Boolean(parsed.allowAccessWithoutPayment),
      canAccessRobots: Boolean(parsed.canAccessRobots),
    };
  } catch {
    return null;
  }
}

function writeClientPortalFlags(flags) {
  try {
    sessionStorage.setItem(CLIENT_PORTAL_FLAGS_KEY, JSON.stringify(flags));
  } catch {
    /* ignore */
  }
}

export function getPersistedClientPortalFlags() {
  return readClientPortalFlags();
}

export function persistClientPortalFlags(flags) {
  writeClientPortalFlags(flags);
}

/** Merge API portal flags with persisted values — missing fields must not clear access on refresh. */
export function mergeClientPortalFlags(apiData = {}, persisted = null) {
  const prev = persisted || readClientPortalFlags() || {};
  const pick = (key) =>
    apiData[key] !== undefined ? Boolean(apiData[key]) : Boolean(prev[key]);
  return {
    allowUserAppOnWeb: pick('allowUserAppOnWeb'),
    allowAccessWithoutPayment: pick('allowAccessWithoutPayment'),
    canAccessRobots: pick('canAccessRobots'),
  };
}

function getLocalStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function getStoredToken(key) {
  const storage = getLocalStorage();
  if (!storage) return null;
  try {
    return decodeTokenFromStorage(storage.getItem(key));
  } catch {
    return null;
  }
}

function setStoredToken(key, value) {
  const storage = getLocalStorage();
  if (!storage || !value) return;
  try {
    const encoded = encodeTokenForStorage(value);
    if (encoded) storage.setItem(key, encoded);
  } catch {
    /* ignore */
  }
}

function removeStoredToken(key) {
  const storage = getLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** One-time migration for sessions that still have tokens in cookies. */
function migrateCookiesToLocalStorage() {
  if (typeof document === 'undefined') return;
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    const readCookie = (name) => {
      const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    };

    const accessFromCookie = readCookie(ACCESS_TOKEN_KEY);
    const refreshFromCookie = readCookie(REFRESH_TOKEN_KEY);

    if (accessFromCookie && !storage.getItem(ACCESS_TOKEN_KEY)) {
      setStoredToken(ACCESS_TOKEN_KEY, accessFromCookie);
    }
    if (refreshFromCookie && !storage.getItem(REFRESH_TOKEN_KEY)) {
      setStoredToken(REFRESH_TOKEN_KEY, refreshFromCookie);
    }

    if (accessFromCookie || refreshFromCookie) {
      document.cookie = `${ACCESS_TOKEN_KEY}=; path=/; max-age=0`;
      document.cookie = `${REFRESH_TOKEN_KEY}=; path=/; max-age=0`;
    }
  } catch {
    /* ignore */
  }
}

/** Re-encode legacy plain JWT values already in localStorage. */
function migratePlainTokensToEncoded() {
  const storage = getLocalStorage();
  if (!storage) return;

  [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY].forEach((key) => {
    try {
      const raw = storage.getItem(key);
      if (!raw || raw.startsWith(TOKEN_STORAGE_PREFIX)) return;
      const plain = decodeTokenFromStorage(raw);
      if (plain) setStoredToken(key, plain);
      else storage.removeItem(key);
    } catch {
      /* ignore */
    }
  });
}

migrateCookiesToLocalStorage();
migratePlainTokensToEncoded();

export function getAccessToken() {
  return getStoredToken(ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  return getStoredToken(REFRESH_TOKEN_KEY);
}

export function setAccessToken(accessToken) {
  setStoredToken(ACCESS_TOKEN_KEY, accessToken);
}

export function setRefreshToken(refreshToken) {
  setStoredToken(REFRESH_TOKEN_KEY, refreshToken);
}

export function setAuthTokens({ accessToken, refreshToken } = {}) {
  const token = accessToken || '';
  if (token) setAccessToken(token);
  if (refreshToken) setRefreshToken(refreshToken);
}

export function updateAccessToken(accessToken) {
  setAccessToken(accessToken);
}

export function clearAuthTokens() {
  removeStoredToken(ACCESS_TOKEN_KEY);
  removeStoredToken(REFRESH_TOKEN_KEY);
}

export function isAuthenticated() {
  return !!(getAccessToken() || getRefreshToken());
}

export function persistLoginSession(data, email) {
  setAuthTokens({
    accessToken: data.accessToken || data.token || '',
    refreshToken: data.refreshToken || '',
  });
  const portalFlags = {
    allowUserAppOnWeb: Boolean(data.allowUserAppOnWeb),
    allowAccessWithoutPayment: Boolean(data.allowAccessWithoutPayment),
    canAccessRobots: Boolean(data.canAccessRobots),
  };
  setSession({
    email,
    role: data.role || '',
    ...portalFlags,
    subscriptionAdmin: data.subscriptionAdmin || null,
  });
  if (String(data.role ?? '').trim().toLowerCase() === 'user') {
    writeClientPortalFlags(portalFlags);
  }
}

export function clearSessionStorage() {
  clearAuthTokens();
  clearSession();
  try {
    sessionStorage.removeItem(CLIENT_PORTAL_FLAGS_KEY);
  } catch {
    /* ignore */
  }
}

export const AUTH_PATHS = [
  '/user/validate',
  '/user/verify-otp',
  '/user/login',
  '/user/reset-password',
  '/user/forget-password',
  '/user/password-verify-otp',
  '/user/password-reset',
  '/user/refresh-token',
  '/user/logout',
];

export function isAuthEndpoint(url = '') {
  return AUTH_PATHS.some((path) => url.includes(path));
}

export function isAccessTokenExpiredMessage(message) {
  const text = String(message ?? '').toLowerCase();
  return (
    text === 'access token has expired' ||
    text.includes('token expired') ||
    text.includes('jwt expired')
  );
}
